import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create a base axios instance for addon operations
const adminAddonApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
});

// Request interceptor to add token
adminAddonApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminAddonApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminAddonService = {
  // ==================== COMPATIBLE PRODUCTS ====================
  /**
   * Get all products that can have add-ons (standalone products AND standalone collections)
   * This includes:
   * - Products with typeOfProduct = 'standalone'
   * - Collections with collectionType = 'standalone'
   */
  getCompatibleProducts: async (params = {}) => {
    console.log('📤 [AddonService] Fetching compatible products...');
    try {
      const response = await adminAddonApi.get('/addons/compatible-products', { params });
      console.log('📥 [AddonService] Compatible products fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error fetching compatible products:', error);
      throw error;
    }
  },

  // ==================== ADDONS CRUD ====================
  /**
   * Get all add-ons with filters and pagination
   */
  getAddons: async (params = {}) => {
    console.log('📤 [AddonService] Fetching add-ons...');
    try {
      const response = await adminAddonApi.get('/addons', { params });
      console.log('📥 [AddonService] Add-ons fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error fetching add-ons:', error);
      throw error;
    }
  },

  /**
   * Get add-on by ID
   */
  getAddonById: async (addonId) => {
    console.log('📤 [AddonService] Fetching add-on:', addonId);
    try {
      const response = await adminAddonApi.get(`/addons/${addonId}`);
      console.log('📥 [AddonService] Add-on fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error fetching add-on:', error);
      throw error;
    }
  },

  /**
   * Create new add-on
   */
  createAddon: async (addonData) => {
    console.log('📤 [AddonService] Creating add-on:', addonData);
    try {
      const response = await adminAddonApi.post('/addons', addonData);
      console.log('📥 [AddonService] Add-on created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error creating add-on:', error);
      throw error;
    }
  },

  /**
   * Update add-on
   */
  updateAddon: async (addonId, addonData) => {
    console.log('📤 [AddonService] Updating add-on:', addonId);
    try {
      const response = await adminAddonApi.put(`/addons/${addonId}`, addonData);
      console.log('📥 [AddonService] Add-on updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error updating add-on:', error);
      throw error;
    }
  },

  /**
   * Update add-on status
   */
  updateAddonStatus: async (addonId, status) => {
    console.log('📤 [AddonService] Updating add-on status:', addonId, status);
    try {
      const response = await adminAddonApi.patch(`/addons/${addonId}/status`, { status });
      console.log('📥 [AddonService] Add-on status updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error updating add-on status:', error);
      throw error;
    }
  },

  /**
   * Delete add-on (soft delete - archive)
   */
  deleteAddon: async (addonId) => {
    console.log('📤 [AddonService] Deleting add-on:', addonId);
    try {
      const response = await adminAddonApi.delete(`/addons/${addonId}`);
      console.log('📥 [AddonService] Add-on deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error deleting add-on:', error);
      throw error;
    }
  },

  /**
   * Get add-on statistics
   */
  getAddonStats: async () => {
    console.log('📤 [AddonService] Fetching add-on statistics...');
    try {
      const response = await adminAddonApi.get('/addons/stats');
      console.log('📥 [AddonService] Add-on statistics fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error fetching add-on statistics:', error);
      throw error;
    }
  },

  /**
   * Search add-ons
   */
  searchAddons: async (query, params = {}) => {
    console.log('📤 [AddonService] Searching add-ons:', query);
    try {
      const response = await adminAddonApi.get('/addons/search', {
        params: { ...params, query }
      });
      console.log('📥 [AddonService] Add-ons search results:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error searching add-ons:', error);
      throw error;
    }
  },

  /**
   * Bulk update add-ons
   */
  bulkUpdateAddons: async (addonIds, data) => {
    console.log('📤 [AddonService] Bulk updating add-ons:', addonIds);
    try {
      const response = await adminAddonApi.post('/addons/bulk-update', {
        addonIds,
        data
      });
      console.log('📥 [AddonService] Add-ons bulk updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error bulk updating add-ons:', error);
      throw error;
    }
  },

  /**
   * Export add-ons
   */
  exportAddons: async (format = 'csv') => {
    console.log('📤 [AddonService] Exporting add-ons...');
    try {
      const response = await adminAddonApi.get('/addons/export', {
        params: { format },
        responseType: 'blob'
      });
      console.log('📥 [AddonService] Add-ons exported');
      return response.data;
    } catch (error) {
      console.error('💥 [AddonService] Error exporting add-ons:', error);
      throw error;
    }
  },

  // ==================== UTILITIES ====================
  /**
   * Upload image to Cloudinary
   */
  uploadImageToCloudinary: async (file, uploadPreset = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset || process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        }
      );
      
      return {
        url: response.data.secure_url,
        publicId: response.data.public_id
      };
    } catch (error) {
      console.error('💥 [AddonService] Error uploading to Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Format price
   */
  formatPrice: (price) => {
    return `₦${parseFloat(price || 0).toLocaleString()}`;
  },

  /**
   * Get status color
   */
  getStatusColor: (status) => {
    const colors = {
      active: 'green',
      draft: 'yellow',
      inactive: 'gray',
      archived: 'red'
    };
    return colors[status] || 'gray';
  },

  /**
   * Format date
   */
  formatDate: (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  /**
   * Validate ObjectId
   */
  isValidObjectId: (id) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  }
};

export default adminAddonService;